export const USER_ACCESS_MODES = {
  STANDARD: 'STANDARD',
  VENDOR: 'VENDOR',
  PRINT_ONLY: 'PRINT_ONLY',
  NONE: 'NONE'
};

const IGNORED_ACCESS_ROLES = ['Everyone'];
const TAG_ONLY_ROLES = ['PRINT_TAG', 'BRAND_TAG'];

const normalizeGroups = (groups) => (Array.isArray(groups) ? groups : []);

const isTagOnly = (groups) => {
  const meaningfulGroups = groups.filter((group) => !IGNORED_ACCESS_ROLES.includes(group));
  return meaningfulGroups.length > 0 && meaningfulGroups.every((group) => TAG_ONLY_ROLES.includes(group));
};

const tagOnlyLandingPath = (groups) => (groups.includes('PRINT_TAG') ? '/sales/print-tag' : '/sales/brand-tag');

/**
 * Builds a centralized access profile from Auth0 UI group membership.
 *
 * Precedence: SUPER_ADMIN → STANDARD; only PRINT_TAG and/or BRAND_TAG (Everyone ignored)
 * → PRINT_ONLY; VENDOR → VENDOR; other assigned roles → STANDARD; empty/invalid input → NONE.
 * PRINT_TAG / BRAND_TAG combined with a non-vendor role keeps that menu and still grants
 * the matching tag page. VENDOR never grants Print Tag or Brand Tag, even when those
 * roles are also assigned.
 *
 * @param {unknown} groups - The signed-in user's UI group membership collection.
 * @returns {{
 *   mode: string,
 *   landingPath: string|null,
 *   canAccessPrintTag: boolean,
 *   canAccessBrandTag: boolean,
 *   canAccessAuxiliaryRoutes: boolean
 * }}
 */
export function createUserAccessProfile(groups) {
  const normalizedGroups = normalizeGroups(groups);

  if (normalizedGroups.length === 0) {
    return {
      mode: USER_ACCESS_MODES.NONE,
      landingPath: null,
      canAccessPrintTag: false,
      canAccessBrandTag: false,
      canAccessAuxiliaryRoutes: false
    };
  }

  if (normalizedGroups.includes('SUPER_ADMIN')) {
    return {
      mode: USER_ACCESS_MODES.STANDARD,
      landingPath: '/welcome',
      canAccessPrintTag: true,
      canAccessBrandTag: true,
      canAccessAuxiliaryRoutes: true
    };
  }

  if (isTagOnly(normalizedGroups)) {
    return {
      mode: USER_ACCESS_MODES.PRINT_ONLY,
      landingPath: tagOnlyLandingPath(normalizedGroups),
      canAccessPrintTag: normalizedGroups.includes('PRINT_TAG'),
      canAccessBrandTag: normalizedGroups.includes('BRAND_TAG'),
      canAccessAuxiliaryRoutes: false
    };
  }

  const canAccessPrintTag = normalizedGroups.includes('PRINT_TAG');
  const canAccessBrandTag = normalizedGroups.includes('BRAND_TAG');

  if (normalizedGroups.includes('VENDOR')) {
    return {
      mode: USER_ACCESS_MODES.VENDOR,
      landingPath: '/purchases/asn',
      canAccessPrintTag: false,
      canAccessBrandTag: false,
      canAccessAuxiliaryRoutes: true
    };
  }

  return {
    mode: USER_ACCESS_MODES.STANDARD,
    landingPath: '/welcome',
    canAccessPrintTag,
    canAccessBrandTag,
    canAccessAuxiliaryRoutes: true
  };
}

export default createUserAccessProfile;
